import { useEffect, useRef, useState } from "react";
import {
	Radio as AriaRadio,
	RadioGroup as AriaRadioGroup,
} from "react-aria-components";

import {
	Dark,
	Light,
	System,
} from "@/components/modals/base-components/appearances";
import { RadioButtonBase } from "@/components/ui/radio-buttons/radio-buttons";
import { GeneralModal } from "@/features/modals/general-modal";
import { useSettings } from "@/hooks/useSettings";
import { ContrastModes } from "@/types/settings";
import { cx } from "@/utils/cx";

interface ColorThemeModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const ColorThemeModal = ({ isOpen, onClose }: ColorThemeModalProps) => {
	const { contrastMode, setContrastMode } = useSettings();
	const [mode, setMode] = useState<string>(contrastMode);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	// Sync mode state with contrastMode when modal opens or contrastMode changes
	useEffect(() => {
		if (isOpen) {
			// Update state asynchronously to avoid cascading renders
			queueMicrotask(() => {
				setMode(contrastMode);
			});
		}
	}, [isOpen, contrastMode]);

	// Prevent scroll jump when clicking radio buttons (but allow keyboard navigation to scroll)
	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		let isMouseInteraction = false;
		let savedScrollTop = 0;

		const handleMouseDown = () => {
			isMouseInteraction = true;
			savedScrollTop = container.scrollTop;
		};

		const handleKeyDown = () => {
			isMouseInteraction = false;
		};

		const handleFocus = (e: FocusEvent) => {
			const target = e.target as HTMLElement;
			if (target.closest('[role="radio"]') && isMouseInteraction) {
				// Restore scroll position after browser's scroll-into-view for mouse clicks
				requestAnimationFrame(() => {
					if (container) {
						container.scrollTop = savedScrollTop;
					}
				});
			}
		};

		// Track mouse interactions on the container
		container.addEventListener("mousedown", handleMouseDown, true);
		container.addEventListener("keydown", handleKeyDown, true);
		container.addEventListener("focusin", handleFocus, true);

		return () => {
			container.removeEventListener("mousedown", handleMouseDown, true);
			container.removeEventListener("keydown", handleKeyDown, true);
			container.removeEventListener("focusin", handleFocus, true);
		};
	}, [isOpen]);

	return (
		<GeneralModal
			buttonSize="lg"
			cancelText="Cancel"
			closeOnSubmit
			contentRef={scrollContainerRef}
			description="Switch between light and dark theme."
			isOpen={isOpen}
			key={contrastMode}
			onClose={onClose}
			onSubmit={() => {
				setContrastMode(mode as ContrastModes);
			}}
			panelClassName="sm:max-w-172"
			submitText="Save changes"
			title="Choose Color Theme"
		>
			<div className="flex flex-col">
				<AriaRadioGroup
					aria-describedby="Switch between light and dark modes."
					aria-label="Display preference"
					className="-mx-4 scrollbar-hide flex flex-col gap-5 overflow-x-auto px-4 pt-5 sm:flex-row sm:pt-6"
					onChange={setMode}
					value={mode}
				>
					<AriaRadio
						className="flex w-full cursor-pointer flex-col gap-3"
						value="system"
					>
						{(props) => (
							<>
								<span className="relative">
									<System
										className={cx(
											"z-20",
											props.isSelected &&
												"h-full w-full rounded-[10px] outline-2 outline-offset-2 outline-focus-ring",
										)}
									/>
									{props.isSelected && (
										<RadioButtonBase
											{...props}
											className="absolute bottom-2 left-2"
											size="md"
										/>
									)}
								</span>
								<p className="text-sm font-semibold text-primary">
									System preference
								</p>
							</>
						)}
					</AriaRadio>
					<AriaRadio
						className="flex w-full cursor-pointer flex-col gap-3"
						value="light"
					>
						{(props) => (
							<>
								<span className="relative">
									<Light
										className={cx(
											"z-20",
											props.isSelected &&
												"rounded-[10px] outline-2 outline-offset-2 outline-focus-ring",
										)}
									/>
									{props.isSelected && (
										<RadioButtonBase
											{...props}
											className="absolute bottom-2 left-2"
											size="md"
										/>
									)}
								</span>
								<p className="text-sm font-semibold text-primary">Light mode</p>
							</>
						)}
					</AriaRadio>
					<AriaRadio
						className="flex w-full cursor-pointer flex-col gap-3"
						value="dark"
					>
						{(props) => (
							<>
								<span className="relative">
									<Dark
										className={cx(
											"z-20",
											props.isSelected &&
												"rounded-[10px] outline-2 outline-offset-2 outline-focus-ring",
										)}
									/>
									{props.isSelected && (
										<RadioButtonBase
											{...props}
											className="absolute bottom-2 left-2"
											size="md"
										/>
									)}
								</span>
								<p className="text-sm font-semibold text-primary">Dark mode</p>
							</>
						)}
					</AriaRadio>
				</AriaRadioGroup>
			</div>
		</GeneralModal>
	);
};
